import { useEffect, useRef, useState } from "react";

const wsUrl = () => {
  const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${base.replace(/^http/, "ws")}/api/ws/quotes`;
};

/** One websocket per hook instance. Returns { SYMBOL: {price, change_pct, source} }.
 *  Falls back silently to whatever polling the page already does if the socket
 *  can't connect, so no screen ever ends up with a frozen price. */
export const useLiveQuotes = (symbols = []) => {
  const [quotes, setQuotes] = useState({});
  const [connected, setConnected] = useState(false);
  const ws = useRef(null);
  const retry = useRef(null);
  const attempt = useRef(0);
  const key = symbols.filter(Boolean).join(",");

  useEffect(() => {
    if (!key) return undefined;
    let closed = false;

    const connect = () => {
      const socket = new WebSocket(wsUrl());
      ws.current = socket;

      socket.onopen = () => {
        attempt.current = 0;
        setConnected(true);
        socket.send(JSON.stringify({ symbols: key.split(",") }));
      };
      socket.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type !== "tick") return;
        setQuotes((prev) => {
          const next = { ...prev };
          msg.quotes.forEach((q) => { next[q.symbol] = q; });
          return next;
        });
      };
      socket.onclose = () => {
        setConnected(false);
        if (closed) return;
        attempt.current += 1;
        const wait = Math.min(1000 * 2 ** attempt.current, 30000);
        retry.current = setTimeout(connect, wait);
      };
      socket.onerror = () => socket.close();
    };

    connect();
    return () => {
      closed = true;
      clearTimeout(retry.current);
      ws.current?.close();
    };
  }, [key]);

  return { quotes, connected, live: Object.values(quotes).some((q) => q.source === "alpaca_iex") };
};
